import React, {
  FC, useEffect
} from 'react';
import clsx from 'clsx';
import {
  useForm
} from 'react-hook-form';
import {
  zodResolver
} from '@hookform/resolvers/zod';
import {
  z
} from 'zod';
import {
  useTranslation
} from 'react-i18next';
import {
  useLocation
} from 'react-router-dom';

import {
  Button,
  ModalContainer,
  Spinner,
  successfulToast,
} from 'src/shared/components';
import {
  showWarningFromServer
} from 'src/shared/utils';
import {
  usePostApiOrganizationsByIdSuggestionsMutation
} from 'src/redux/openapi';
import {
  CheckIcon
} from 'src/shared/icons';

interface SuggestQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  suggestionTitle: string | null;
}

const schema = z.object({
  text: z.string().min(1).max(2000),
});

type FormValues = z.infer<typeof schema>;

export const SuggestQuestionModal: FC<SuggestQuestionModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  suggestionTitle,
}) => {
  const {
    t
  } = useTranslation();

  const location = useLocation();

  const {
    register,
    handleSubmit,
    reset,
    formState: {
      errors
    },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      text: '',
    },
  });

  const [submitSuggestion, {
    isLoading, isSuccess
  }] = usePostApiOrganizationsByIdSuggestionsMutation();

  useEffect(
    () => {
      if (isSuccess) {
        successfulToast(t('suggestions.suggestQuestionSuccess'));
        reset();
        onClose();
      }
    },
    [isSuccess]
  );

  const onSubmit = handleSubmit(async ({
    text
  }) => {
    try {
      await submitSuggestion({
        id: organizationId,
        body: {
          text,
          pageUrl: `${window.location.origin}${location.pathname}${location.search}`,
        },
      }).unwrap();
    } catch (error) {
      showWarningFromServer(error);
    }
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <ModalContainer
      isOpen={isOpen}
      modalTitle={t('suggestions.suggestion')}
      closeModalHandler={handleClose}
      modalWidth={500}
    >
      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-6"
      >
        {suggestionTitle && (
          <p className="text-15-20 font-medium text-dark-gray">
            {suggestionTitle}
          </p>
        )}

        <div>
          <div className="block text-13-21 md:text-15-20 font-medium text-grey-dark-40">
            {t('suggestions.suggestion')}
          </div>

          <div
            className={clsx(
              'rounded border mt-2',
              errors.text
                ? 'bg-red-bg-error border-red-border-error'
                : 'bg-alice-blue border-gray94 focus-within:bg-input-blue focus-within:border-input-border-blue',
            )}
          >
            <textarea
              {...register('text')}
              placeholder={t('suggestions.suggestQuestionPlaceholder')}
              rows={5}
              className="block w-full py-3 px-4 text-15-20 md:text-17-26 text-gray7 border-0 focus:outline-none bg-transparent resize-none rounded"
            />
          </div>

          {errors.text && (
            <div className="text-13-21 font-medium text-red-text-error mt-1">
              {errors.text.message}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            variant="big-blue"
            type="submit"
            className="w-max"
          >
            {isLoading ? (
              <Spinner
                color="white"
                size={18}
                centered={false}
              />
            ) : (
              <>
                {t('buttons.send')}

                <CheckIcon className="w-4 h-4 ml-1.5 text-white" />
              </>
            )}
          </Button>
        </div>
      </form>
    </ModalContainer>
  );
};
